/*
Greedy walking-route planner.

Given the user's current GPS, a target species, and a desired walk
length, returns an ordered list of trees to visit + the polyline
that links them. The order is built greedily — at each step pick
the closest matching tree that hasn't been visited yet — until the
cumulative path length crosses the target distance, the candidate
set runs out, or we hit the safety waypoint cap.

Greedy nearest-neighbour has well-known pathologies (the last leg
can be nasty), but for personal-use routes of 1-5 km / 8-30 stops
it produces routes a human would walk happily. A 2-opt local
refinement could be added later — that's the standard next step
when greedy starts feeling jagged.

The planner is a pure function — module-level state lives in
`routeState.ts`, not here.
*/

import type { ArbreFeature } from './arbresFeatures'

const kMaxWaypoints: number = 30  // hard cap so a 5 km route in a high-density species doesn't queue 100 stops
const kEarthRadiusMeters: number = 6_371_000

export interface RoutePlan
{
	speciesIndex: number
	startLat: number
	startLon: number
	targetMeters: number  // the user's request
	totalMeters: number   // cumulative walked distance, start → last waypoint
	waypoints: ArbreFeature[]  // ordered visit list, first is the closest to start
	polyline: [number, number][]  // [lon, lat] sequence including the start point — fed straight to MapLibre
}

export interface PlanRouteOptions
{
	startLat: number
	startLon: number
	speciesIndex: number
	targetMeters: number
	allFeatures: readonly ArbreFeature[]  // typically the cached fFeatures from arbresFeatures.ts
}

/*
Plan a route. Returns null when no candidate trees exist for the
species (e.g. the user filtered to a species the dataset doesn't
have, or every individual was dropped by upstream filters). Otherwise
returns the plan even if `targetMeters` couldn't be reached — the
caller decides whether the partial route is good enough.
*/
export function planRoute(inOpts: PlanRouteOptions): RoutePlan | null
{
	const vCandidates = inOpts.allFeatures.filter((inF) => inF.speciesIndex === inOpts.speciesIndex)
	if (vCandidates.length === 0) return null

	// Visited set is by arbreId — duplicates between fFeatures shouldn't
	// happen, but a Set keeps the inner loop's "skip already-picked"
	// branchless.
	const vVisited = new Set<number>()
	const vWaypoints: ArbreFeature[] = []
	const vPolyline: [number, number][] = [[inOpts.startLon, inOpts.startLat]]
	let vCursorLat = inOpts.startLat
	let vCursorLon = inOpts.startLon
	let vTotalMeters: number = 0

	while (vWaypoints.length < kMaxWaypoints && vTotalMeters < inOpts.targetMeters)
	{
		let vBest: ArbreFeature | null = null
		let vBestDist: number = Number.POSITIVE_INFINITY
		for (const vF of vCandidates)
		{
			if (vVisited.has(vF.id)) continue
			const vD = haversineMeters(vCursorLat, vCursorLon, vF.lat, vF.lon)
			if (vD < vBestDist) { vBestDist = vD; vBest = vF }
		}
		if (!vBest) break  // exhausted the candidate set before hitting the target
		vVisited.add(vBest.id)
		vWaypoints.push(vBest)
		vPolyline.push([vBest.lon, vBest.lat])
		vTotalMeters += vBestDist
		vCursorLat = vBest.lat
		vCursorLon = vBest.lon
	}

	if (vWaypoints.length === 0) return null  // every candidate was somehow filtered out (shouldn't happen but defensive)

	return {
		speciesIndex: inOpts.speciesIndex,
		startLat: inOpts.startLat,
		startLon: inOpts.startLon,
		targetMeters: inOpts.targetMeters,
		totalMeters: vTotalMeters,
		waypoints: vWaypoints,
		polyline: vPolyline,
	}
}

/*
Great-circle distance via haversine. Inlined here so the planner
doesn't depend on the geolocation module's helper (which is part
of the capture-flow chunk graph).
*/
function haversineMeters(inLat1: number, inLon1: number, inLat2: number, inLon2: number): number
{
	const vDLat = toRadians(inLat2 - inLat1)
	const vDLon = toRadians(inLon2 - inLon1)
	const vSlat = Math.sin(vDLat / 2)
	const vSlon = Math.sin(vDLon / 2)
	const vA = vSlat * vSlat + Math.cos(toRadians(inLat1)) * Math.cos(toRadians(inLat2)) * vSlon * vSlon
	return 2 * kEarthRadiusMeters * Math.asin(Math.min(1, Math.sqrt(vA)))
}

function toRadians(inDeg: number): number { return inDeg * Math.PI / 180 }
